<template>
  <component :is="embeddedInCard ? 'div' : 'Box'">
    <InfoRow
      v-if="showPickupRow"
      icon-slug="pickup"
      :main-text="fulfillmentMethodName"
      :secondary-text="fulfillmentAddressOneLine"
      :secondary-text-link="fulfillmentAddressLink"
      :description-text="fulfillmentMethodDescription"
      :layout="layout"
      :merchant-time-zone="merchantTimeZone"
      @change="changePickupFulfillmentMethod"
    />
    <InfoRow
      v-if="showDeliveryRow"
      icon-slug="delivery"
      :main-text="fulfillmentAddressTopLine"
      :secondary-text="fulfillmentAddressBottomLine"
      :secondary-text-link="fulfillmentAddressLink"
      :description-text="fulfillmentMethodDescription"
      :merchant-time-zone="merchantTimeZone"
      :layout="layout"
      @change="changeAddress"
    />
    <InfoRow
      v-if="showMethodRow"
      icon-slug="method"
      :main-text="fulfillmentMethodName"
      :secondary-text="fulfillmentMethodPrice"
      :options="possibleFulfillmentMethodsForAddress"
      :selected-option="fulfillmentMethod?.id"
      options-type="fulfillment_methods"
      :layout="layout"
      :merchant-time-zone="merchantTimeZone"
      :loading="isLoadingMethod && showInlineDateLoading"
      @change="changeDeliveryFulfillmentMethod"
      @clicked-option="selectedNewFulfillmentMethod"
    />
    <DateRow
      v-if="showDateRow"
      :layout="layout"
      :hide-times="hideTimes"
      :options="filteredFulfillmentSlots"
      :selected-option="fulfillmentSlot?.attributes?.arrives_beginning_estimate"
      :time-options="possibleFulfillmentSlotTimes"
      :selected-time-option="fulfillmentSlot?.id"
      :merchant-time-zone="merchantTimeZone"
      :loading-slot-id="showInlineDateLoading ? loadingSlotId : null"
      :show-inline-date-loading="showInlineDateLoading"
      :hint="dateHint"
      @change="changeFulfillmentSlotDate"
      @clicked-option="selectedNewFulfillmentSlot"
      @time-change="changeFulfillmentSlotTime"
    />
    <InfoRow
      v-if="showMethodChoices"
      icon-slug="method"
      :options="methodsOnSelectedDate"
      :selected-option="awaitingConfirmation ? null : fulfillmentMethod?.id"
      options-type="priced_fulfillment_methods"
      :layout="layout"
      :merchant-time-zone="merchantTimeZone"
      :keep-options-open="true"
      class="border-t"
      :class="{ 'opacity-60 pointer-events-none': isSaving }"
      @clicked-option="chooseMethodOnSelectedDate"
    >
      <template v-if="methodHint" #options-header>
        <FulfillmentChoiceHint :text="methodHint" />
      </template>
    </InfoRow>
    <div v-if="showBottomNoticeCheckout" class="flex">
      <div class="w-full border-b bg-brand-100 text-brand-800 rounded-lg shadow-sm">
        <div
          v-if="fulfillmentSlot?.attributes?.custom_fulfillment_description_unpaid"
          class="leading-5 pt-4 pb-4 px-6 scrolling-touch text-sm text-center"
        >
          {{ fulfillmentSlot?.attributes?.custom_fulfillment_description_unpaid }}
        </div>
        <div v-else class="leading-5 pt-4 pb-4 px-6 scrolling-touch text-sm text-center">
          Order by
          <span class="font-semibold">{{ orderByTime }}&nbsp;</span>
          <span v-if="isPickup">to pick up</span>
          <span v-if="isDelivery">for delivery</span>
          {{ timeVerb }}
          <span class="font-semibold">{{ fulfillmentTime }} {{ merchantTZAbbreviation }}.</span>
        </div>
      </div>
    </div>
    <div v-if="showContinue" class="flex justify-end px-6 pb-4 pt-2">
      <ConfirmFulfillmentSlotButton @confirm="confirmSlot" />
    </div>
    <div v-if="showBottomNoticeReceipt" class="flex">
      <div class="w-full border-b bg-brand-100 text-brand-800 rounded-lg shadow-sm">
        <div
          v-if="fulfillmentSlot?.attributes?.custom_fulfillment_description_receipt"
          class="leading-5 pt-4 pb-4 px-6 scrolling-touch text-sm text-center"
        >
          {{ fulfillmentSlot?.attributes?.custom_fulfillment_description_receipt }}
        </div>
        <div v-else class="leading-5 pt-4 pb-4 px-6 scrolling-touch text-sm text-center">
          <span v-if="isPickup">Pick up</span>
          <span v-if="isDelivery">{{ deliveryTitle }}</span>
          {{ receiptNoticeVerb }}
          <span class="font-semibold">{{ receiptNoticeWhen }}.</span>
        </div>
      </div>
    </div>
  </component>
</template>

<script>
let strftime = require('strftime')
import AddressLine from '@/components/Partials/AddressLine'
import InfoRow from '@/components/Checkout/InfoRow'
import DateRow from '@/components/Checkout/DateRow'
import ConfirmFulfillmentSlotButton from '@/components/Checkout/ConfirmFulfillmentSlotButton'
import FulfillmentChoiceHint from '@/components/Checkout/FulfillmentChoiceHint'
import Box from '@/components/Molecules/Box'
import isInFuture from '@/utils/dateHelper'
import { serverDayToDateObject } from '@/helpers/dates'
import OrderSummarySection from '@/components/Checkout/OrderSummarySection'
import mapRelationship from '@/utils/mapRelationship'
import {
  offerableMethodsForAddress,
  mergedSlotsAcrossMethods,
  methodsWithSelectableSlotOn,
  selectableSlotIdOnDate,
  methodPriceLabel
} from '@/utils/fulfillmentSlots'
import { isDeliveryLikeType, fulfillmentTitleFor } from '@/utils/fulfillmentTypes'

export default {
  name: 'FulfillmentSelectedModule',
  components: {
    AddressLine,
    Box,
    InfoRow,
    DateRow,
    ConfirmFulfillmentSlotButton,
    FulfillmentChoiceHint,
    OrderSummarySection
  },
  props: {
    layout: {
      type: String,
      required: true
    },
    passedBottle: {
      type: Object,
      required: false
    },
    embeddedInCard: {
      type: Boolean,
      default: false
    },
    showInlineDateLoading: {
      type: Boolean,
      default: false
    },
    awaitingConfirmation: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isSaving: false,
      loadingSlotId: null,
      isLoadingMethod: false
    }
  },
  computed: {
    merchantTZAbbreviation() {
      if (!this.merchantAndBrowserTZMatches) {
        return `(${
          new Date().toLocaleTimeString('en-US', { timeZone: this.merchantTZ, timeZoneName: 'short' }).split(' ')[2]
        })`
      } else {
        return ''
      }
    },
    merchantTZ() {
      return mapRelationship(this.$store.state, this.bottle.relationships.merchant.data)?.attributes.time_zone
    },
    merchantAndBrowserTZMatches() {
      return this.merchantTZ === Intl.DateTimeFormat().resolvedOptions().timeZone
    },
    bottle() {
      return this.$store.getters.currentBottle
    },
    store() {
      return mapRelationship(this.$store.state, this.bottle.relationships.store.data)
    },
    merchantTimeZone() {
      return mapRelationship(this.$store.state, this.bottle.relationships.merchant.data)?.attributes.time_zone
    },
    fulfillmentSlot() {
      return this.$store.getters.fulfillmentSlot
    },
    selectNewFulfillmentMethod() {
      return false
    },
    fulfillmentMethod() {
      return this.$store.getters.fulfillmentMethod
    },
    calculatedFulfillmentSlots() {
      if (this.mergeableDeliveryMethods.length > 0) {
        return mergedSlotsAcrossMethods(
          this.mergeableDeliveryMethods,
          this.resolveRelationship,
          this.fulfillmentMethod.id
        )
      }
      return mapRelationship(this.$store.state, this.fulfillmentMethod.relationships.fulfillment_slots_detail.data)
        ?.attributes?.calculated_fulfillment_slots
    },
    mergeableDeliveryMethods() {
      if (!this.isDelivery) return []
      const methods = this.possibleFulfillmentMethodsForAddress
      const includesCurrentMethod = methods.some((method) => method.id == this.fulfillmentMethod?.id)
      const everyMethodShowsDates = methods.every((method) => !method.attributes?.hide_times)
      return methods.length > 1 && includesCurrentMethod && everyMethodShowsDates ? methods : []
    },
    selectedSlotDate() {
      return this.fulfillmentSlot?.attributes?.arrives_beginning_estimate_date
    },
    datesLeadToMethod() {
      return this.layoutCheckout && this.mergeableDeliveryMethods.length > 0
    },
    methodsOnSelectedDate() {
      if (!this.datesLeadToMethod) return []
      return methodsWithSelectableSlotOn(this.mergeableDeliveryMethods, this.resolveRelationship, this.selectedSlotDate)
    },
    showMethodChoices() {
      return this.methodsOnSelectedDate.length > 1
    },
    datesTappable() {
      return (
        this.layoutCheckout &&
        this.showDateRow &&
        (this.filteredFulfillmentSlots?.length > 1 || this.possibleFulfillmentSlotTimes.length > 1)
      )
    },
    selectedDateLabel() {
      if (!this.selectedSlotDate) return ''
      return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(
        serverDayToDateObject(this.selectedSlotDate)
      )
    },
    fulfillmentKind() {
      return fulfillmentTitleFor(this.fulfillmentMethod?.attributes?.type).toLowerCase()
    },
    promptsVisible() {
      return this.awaitingConfirmation && !this.isSaving
    },
    methodHint() {
      if (!this.promptsVisible || !this.showMethodChoices) return null
      return `Choose a ${this.fulfillmentKind} option for ${this.selectedDateLabel}`
    },
    tapConfirmsDate() {
      return this.datesLeadToMethod && !this.showMethodChoices && this.datesTappable
    },
    dateHint() {
      if (!this.promptsVisible || !this.tapConfirmsDate) return null
      return `Tap ${this.selectedDateLabel} to confirm your ${this.fulfillmentKind}, or pick another day`
    },
    showContinue() {
      return this.awaitingConfirmation && !this.isSaving && !this.showMethodChoices && !this.tapConfirmsDate
    },
    filteredFulfillmentSlots() {
      if (this.store?.attributes?.pickup_fulfillment_method_visibility == 'available-only') {
        return this.calculatedFulfillmentSlots?.filter((option) => option.can_select)
      } else {
        return this.calculatedFulfillmentSlots
      }
    },
    possibleFulfillmentMethodsForAddress() {
      return offerableMethodsForAddress(
        this.fulfillmentAddress,
        (stubs) => this.$store.getters.fulfillmentMethods(stubs),
        this.resolveRelationship
      )
    },
    possibleFulfillmentSlotTimes() {
      return this.$store.getters
        .fulfillmentSlots(this.bottle?.relationships?.possible_fulfillment_slot_times?.data)
        .sort((a, b) => {
          const aDate = new Date(a.attributes.arrives_beginning_estimate)
          const bDate = new Date(b.attributes.arrives_beginning_estimate)
          return aDate - bDate
        })
    },
    possibleFulfillmentSlotDays() {
      return this.$store.getters
        .fulfillmentSlots(this.bottle?.relationships?.possible_fulfillment_slot_days?.data)
        .sort((a, b) => {
          return new Date(a.attributes.arrives_beginning_estimate) - new Date(b.attributes.arrives_beginning_estimate)
        })
    },
    isPickup() {
      return this.fulfillmentMethod?.attributes?.type === 'PickupFulfillmentMethod'
    },
    isDelivery() {
      return isDeliveryLikeType(this.fulfillmentMethod?.attributes?.type)
    },
    deliveryTitle() {
      const deliveryDate = new Date(this.fulfillmentSlot?.attributes?.arrives_beginning_estimate)
      return isInFuture(deliveryDate) ? 'Delivery' : 'Delivered'
    },
    fulfillmentAddress() {
      return this.$store.getters.deliveryAddress(this.bottle.relationships?.delivery_address?.data?.id)
    },
    fulfillmentAddressTopLine() {
      let temp = []
      if (this.fulfillmentAddress?.attributes?.address1) temp.push(this.fulfillmentAddress?.attributes?.address1)
      if (this.fulfillmentAddress?.attributes?.address2) temp.push(this.fulfillmentAddress?.attributes?.address2)
      return temp.join(', ')
    },
    fulfillmentAddressBottomLine() {
      let temp = []
      if (this.fulfillmentAddress?.attributes?.city) temp.push(this.fulfillmentAddress?.attributes?.city)
      if (this.fulfillmentAddress?.attributes?.state) temp.push(this.fulfillmentAddress?.attributes?.state)
      if (this.fulfillmentAddress?.attributes?.zip) temp.push(this.fulfillmentAddress?.attributes?.zip)
      if (this.fulfillmentAddress?.attributes?.notes)
        '(' + temp.push(this.fulfillmentAddress?.attributes?.notes).toString() + ')'
      return temp.join(', ')
    },
    fulfillmentAddressOneLine() {
      let temp = []
      if (this.fulfillmentAddress?.attributes?.address1) temp.push(this.fulfillmentAddress?.attributes?.address1)
      if (this.fulfillmentAddress?.attributes?.address2) temp.push(this.fulfillmentAddress?.attributes?.address2)
      if (this.fulfillmentAddress?.attributes?.city) temp.push(this.fulfillmentAddress?.attributes?.city)
      if (this.fulfillmentAddress?.attributes?.state) temp.push(this.fulfillmentAddress?.attributes?.state)
      if (this.fulfillmentAddress?.attributes?.zip) temp.push(this.fulfillmentAddress?.attributes?.zip)
      return temp.join(', ')
    },
    fulfillmentAddressLink() {
      return ['http://maps.google.com/?q=', JSON.stringify(this.fulfillmentAddressOneLine)].join('')
    },
    fulfillmentMethodName() {
      return this.fulfillmentMethod?.attributes?.name
    },
    fulfillmentMethodDescription() {
      return this.fulfillmentMethod?.attributes?.description
    },
    fulfillmentMethodPrice() {
      return methodPriceLabel(this.fulfillmentMethod)
    },
    fulfillmentSlotDayOfWeek() {
      if (
        !this.fulfillmentSlot?.attributes?.fulfillment_date ||
        !this.fulfillmentSlot?.attributes?.arrives_beginning_estimate
      )
        return null

      return new Intl.DateTimeFormat('en-US', {
        weekday: 'long'
      }).format(new Date(this.fulfillmentSlot?.attributes?.arrives_beginning_estimate))
    },
    fulfillmentSlotDate() {
      if (
        !this.fulfillmentSlot?.attributes?.fulfillment_date ||
        !this.fulfillmentSlot?.attributes?.arrives_beginning_estimate
      )
        return null

      return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      }).format(new Date(this.fulfillmentSlot?.attributes?.arrives_beginning_estimate))
    },
    fulfillmentSlotTimeRange() {
      if (
        !this.fulfillmentSlot?.attributes?.fulfillment_date ||
        !this.fulfillmentSlot?.attributes?.arrives_beginning_estimate
      )
        return null
      const beginningDate = new Date(
        new Date(this.fulfillmentSlot?.attributes?.arrives_beginning_estimate).toLocaleString('en-US', {
          timeZone: this.merchantTZ
        })
      )
      let beginningOfRange = strftime('%-I:%M', beginningDate) + strftime('%p', beginningDate).toLowerCase()
      const endDate = new Date(
        new Date(this.fulfillmentSlot?.attributes?.arrives_ending_estimate).toLocaleString('en-US', {
          timeZone: this.merchantTZ
        })
      )
      let endOfRange = strftime('%-I:%M', endDate) + strftime('%p', endDate).toLowerCase()
      if (beginningOfRange === endOfRange) {
        return beginningOfRange + ` ${this.merchantTZAbbreviation}`
      } else {
        return beginningOfRange + ' to ' + endOfRange + ` ${this.merchantTZAbbreviation}`
      }
    },
    timeVerb() {
      if (
        !this.fulfillmentSlot?.attributes?.fulfillment_date ||
        !this.fulfillmentSlot?.attributes?.arrives_beginning_estimate
      )
        return null
      const beginningDate = new Date(this.fulfillmentSlot?.attributes?.arrives_beginning_estimate)
      const endDate = new Date(this.fulfillmentSlot?.attributes?.arrives_ending_estimate)
      if (beginningDate.toDateString() === endDate.toDateString()) {
        return 'on'
      } else {
        return 'between'
      }
    },
    orderByTime() {
      const date = new Date(
        new Date(this.fulfillmentSlot?.attributes?.display_cutoff).toLocaleString('en-US', {
          timeZone: this.merchantTZ
        })
      )
      return strftime('%A, %B %-d at ', date) + strftime('%-I:%M', date) + strftime('%p', date).toLowerCase()
    },
    fulfillmentTime() {
      const startTime = new Date(
        new Date(this.fulfillmentSlot?.attributes?.arrives_beginning_estimate).toLocaleString('en-US', {
          timeZone: this.merchantTZ
        })
      )
      const endTime = new Date(
        new Date(this.fulfillmentSlot?.attributes?.arrives_ending_estimate).toLocaleString('en-US', {
          timeZone: this.merchantTZ
        })
      )
      if (startTime.toString() == endTime.toString()) {
        return (
          strftime('%A, %B %-d at ', startTime) +
          strftime('%-I:%M', startTime) +
          strftime('%p', startTime).toLowerCase()
        )
      } else if (startTime.getDate() === endTime.getDate()) {
        return (
          strftime('%A, %B %-d from ', startTime) +
          strftime('%-I:%M', startTime) +
          strftime('%p to ', startTime).toLowerCase() +
          strftime('%-I:%M', endTime) +
          strftime('%p', endTime).toLowerCase()
        )
      } else {
        return (
          strftime('%A, %B %-d at ', startTime) +
          strftime('%-I:%M and ', startTime) +
          strftime('%A, %B %-d at ', endTime) +
          strftime('%-I:%M', endTime)
        )
      }
    },
    showPickupRow() {
      return this.isPickup
    },
    showDeliveryRow() {
      return this.isDelivery
    },
    showMethodRow() {
      return this.isDelivery && !this.datesLeadToMethod
    },
    showDateRow() {
      return !this.hideTimes || this.layoutReceipt
    },
    showTimeRow() {
      return this.methodHasSlotsOverMultipleTimes
    },
    methodHasSlotsOverMultipleDays() {
      return true
    },
    methodHasSlotsOverMultipleTimes() {
      return this.possibleFulfillmentSlotTimes.length > 1
    },
    layoutReceipt() {
      return this.layout == 'receipt'
    },
    layoutCheckout() {
      return this.layout == 'checkout'
    },
    hideTimes() {
      return !!this.fulfillmentMethod?.attributes?.hide_times
    },
    showBottomNoticeCheckout() {
      return this.layoutCheckout && !this.hideTimes
    },
    showBottomNoticeReceipt() {
      return this.layoutReceipt
    },
    fulfillmentDateOnly() {
      if (
        !this.fulfillmentSlot?.attributes?.fulfillment_date ||
        !this.fulfillmentSlot?.attributes?.arrives_beginning_estimate
      )
        return null

      const startTime = new Date(
        new Date(this.fulfillmentSlot?.attributes?.arrives_beginning_estimate).toLocaleString('en-US', {
          timeZone: this.merchantTZ
        })
      )
      return strftime('%A, %B %-d', startTime)
    },
    receiptNoticeVerb() {
      return this.hideTimes ? 'on' : this.timeVerb
    },
    receiptNoticeWhen() {
      if (this.hideTimes) return this.fulfillmentDateOnly
      return `${this.fulfillmentTime} ${this.merchantTZAbbreviation}`.trim()
    }
  },
  mounted() {},
  methods: {
    changePickupFulfillmentMethod() {
      this.$emit('selectNewFulfillmentOption', { method: 'selected', bottle: this.passedBottle })
    },
    changeAddress() {
      this.$emit('selectNewFulfillmentOption', { method: 'address', bottle: this.passedBottle })
    },
    changeDeliveryFulfillmentMethod() {
      // this.$emit('selectNewFulfillmentOption', 'pickup')
    },
    changeFulfillmentSlotDate() {},
    changeFulfillmentSlotTime() {},
    trackSave(request) {
      this.isSaving = true
      this.$emit('showLoading', true)
      return request.finally(() => {
        this.isSaving = false
        this.$emit('showLoading', false)
      })
    },
    saveFulfillmentSlot(slotId) {
      return this.trackSave(
        this.$store.dispatch('selectFulfillmentSlot', { newSlotId: slotId, bottleId: this.bottle.id })
      )
    },
    selectedNewFulfillmentSlot(newSlotID) {
      if (this.showInlineDateLoading) {
        this.loadingSlotId = newSlotID
      }
      const date = this.filteredFulfillmentSlots?.find((option) => option.fulfillment_slot_id == newSlotID)?.date
      const leadsToMethodChoice =
        this.datesLeadToMethod &&
        methodsWithSelectableSlotOn(this.mergeableDeliveryMethods, this.resolveRelationship, date).length > 1
      if (leadsToMethodChoice) this.$emit('slotAwaitingConfirmation')
      this.saveFulfillmentSlot(newSlotID)
        .then(
          () => {
            if (!leadsToMethodChoice) this.$emit('slotSelected')
          },
          () => {}
        )
        .finally(() => {
          this.loadingSlotId = null
        })
    },
    chooseMethodOnSelectedDate(methodId) {
      if (methodId == this.fulfillmentMethod?.attributes?.id) {
        this.confirmSlot()
        return
      }
      const method = this.methodsOnSelectedDate.find((option) => option.attributes?.id == methodId)
      const slotId = selectableSlotIdOnDate(method, this.resolveRelationship, this.selectedSlotDate)
      if (!slotId) return
      this.saveFulfillmentSlot(slotId).then(
        () => this.$emit('slotSelected'),
        () => {}
      )
    },
    confirmSlot() {
      if (!this.isSaving) this.$emit('slotSelected')
    },
    resolveRelationship(stub) {
      return mapRelationship(this.$store.state, stub)
    },
    selectedNewFulfillmentMethod(newMethodID) {
      if (this.showInlineDateLoading) {
        this.isLoadingMethod = true
      }
      this.trackSave(this.$store.dispatch('selectFulfillmentMethod', { methodId: newMethodID, bottle: this.bottle }))
        .catch(() => {})
        .finally(() => {
          this.isLoadingMethod = false
        })
    },
    showLoading(value) {
      this.$emit('showLoading', value)
    }
  }
}
</script>
